<template>
    <form class="two-factor" :class="variant" @submit.prevent="submit">
        <h4 class="two-factor__title">{{ $t('auth.twoFactorTitle') }}</h4>
        <p class="two-factor__subtitle">{{ subtitle }}</p>

        <div v-if="mode === 'choose'" class="two-factor__choices">
            <button
                class="two-factor__continue"
                type="button"
                :disabled="busy"
                @click="$emit('choose', 'email')"
            >
                {{ $t('auth.twoFactorChooseEmail') }}
            </button>
            <button
                class="two-factor__continue two-factor__continue--second"
                type="button"
                :disabled="busy"
                @click="$emit('choose', 'authenticator')"
            >
                {{ $t('auth.twoFactorChooseAuthenticator') }}
            </button>
        </div>

        <template v-else>
            <div v-if="mode === 'setup' && qrDataUrl" class="two-factor__qr">
                <img :src="qrDataUrl" :alt="$t('auth.twoFactorQrAlt')" />
            </div>
            <label class="two-factor__label" for="two-factor-code">
                {{ $t('auth.twoFactorCode') }}
            </label>
            <input
                id="two-factor-code"
                ref="codeInput"
                v-model="code"
                class="two-factor__input"
                type="text"
                inputmode="numeric"
                autocomplete="one-time-code"
                maxlength="6"
                :placeholder="$t('auth.twoFactorPlaceholder')"
                @input="onInput"
            />
            <p v-if="localError" class="two-factor__pending">
                {{ $t('auth.twoFactorCodeRequired') }}
            </p>
            <button class="two-factor__continue" type="submit" :disabled="busy">
                {{ busy ? $t('auth.sending') : $t('auth.twoFactorContinue') }}
            </button>
            <button
                v-if="canResend"
                class="two-factor__resend"
                type="button"
                :disabled="busy"
                @click="resend"
            >
                {{ $t('auth.twoFactorResend') }}
            </button>
        </template>

        <p v-if="mode !== 'choose' && !localError && error" class="two-factor__pending">{{ error }}</p>
        <p v-else-if="mode === 'choose' && error" class="two-factor__pending">{{ error }}</p>
        <p v-else-if="resent && canResend" class="two-factor__hint">{{ $t('auth.twoFactorResent') }}</p>

        <button class="two-factor__back" type="button" @click="$emit('back')">
            {{ $t('auth.backToLogin') }}
        </button>
    </form>
</template>

<script>
import QRCode from 'qrcode';

const MODES = ['email', 'authenticator', 'choose', 'setup'];

export default {
    name: 'TwoFactorStep',
    props: {
        variant: {
            type: String,
            default: 'admin',
        },
        email: {
            type: String,
            default: '',
        },
        method: {
            type: String,
            default: 'email',
        },
        otpauthUri: {
            type: String,
            default: '',
        },
        busy: {
            type: Boolean,
            default: false,
        },
        error: {
            type: String,
            default: '',
        },
        resent: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['continue', 'back', 'resend', 'choose'],
    data() {
        return {
            code: '',
            localError: false,
            qrDataUrl: '',
        };
    },
    computed: {
        mode() {
            if (this.method === 'both' || this.method === 'choose') return 'email';
            if (this.method === 'setup' || (this.method === 'authenticator' && this.otpauthUri)) {
                return 'setup';
            }
            return MODES.includes(this.method) ? this.method : 'email';
        },
        canResend() {
            return this.mode === 'email';
        },
        subtitle() {
            if (this.mode === 'choose') return this.$t('auth.twoFactorChooseSubtitle');
            if (this.mode === 'setup') return this.$t('auth.twoFactorSetupSubtitle');
            if (this.mode === 'authenticator') return this.$t('auth.twoFactorAuthenticatorSubtitle');
            if (this.email) return this.$t('auth.twoFactorSubtitleEmail', { email: this.email });
            return this.$t('auth.twoFactorSubtitle');
        },
    },
    watch: {
        method() {
            this.code = '';
            this.localError = false;
            this.renderQr();
            this.focusCode();
        },
        otpauthUri() {
            this.renderQr();
        },
    },
    mounted() {
        this.renderQr();
        this.focusCode();
    },
    methods: {
        focusCode() {
            this.$nextTick(() => {
                if (this.$refs.codeInput) {
                    this.$refs.codeInput.focus();
                }
            });
        },
        async renderQr() {
            if (this.mode !== 'setup' || !this.otpauthUri) {
                this.qrDataUrl = '';
                return;
            }
            try {
                this.qrDataUrl = await QRCode.toDataURL(this.otpauthUri, {
                    width: 220,
                    margin: 1,
                    errorCorrectionLevel: 'M',
                });
            } catch (error) {
                this.qrDataUrl = '';
                console.error('Erro ao gerar o QR code do autenticador', error);
            }
        },
        onInput(event) {
            const digits = String(event.target.value || '')
                .replace(/\D/g, '')
                .slice(0, 6);
            this.code = digits;
            this.localError = false;
        },
        submit() {
            if (this.mode === 'choose') return;
            if (this.code.length !== 6) {
                this.localError = true;
                return;
            }
            this.$emit('continue', this.code);
        },
        resend() {
            this.localError = false;
            this.$emit('resend');
        },
    },
};
</script>

<style scoped>
    .two-factor {
        text-align: left;
    }
    .two-factor__title {
        text-align: center;
        margin: 0 0 8px;
        color: #2c3e50;
        font-weight: 700;
        font-size: 28px;
        letter-spacing: -0.5px;
    }
    .two-factor__subtitle {
        text-align: center;
        margin: 0 0 28px;
        color: #6c757d;
        font-size: 16px;
        line-height: 1.5;
    }
    .two-factor__label {
        display: block;
        font-weight: 600;
        color: #495057;
        margin-bottom: 8px;
        font-size: 14px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }
    .two-factor__input {
        width: 100%;
        box-sizing: border-box;
        border: 2px solid #e9ecef;
        border-radius: 12px;
        padding: 12px 16px;
        font-size: 22px;
        letter-spacing: 0.4em;
        text-align: center;
        background-color: #f8f9fa;
        color: #2c3e50;
    }
    .two-factor__input:focus {
        outline: none;
        border-color: #19c7d0;
        box-shadow: 0 0 0 0.2rem rgba(25, 199, 208, 0.25);
        background-color: white;
    }
    .two-factor__hint,
    .two-factor__pending {
        margin: 12px 0 0;
        font-size: 14px;
        line-height: 1.4;
        text-align: center;
    }
    .two-factor__hint {
        color: #6c757d;
    }
    .two-factor__pending {
        color: #856404;
        background: #fff3cd;
        border: 1px solid #ffeeba;
        border-radius: 8px;
        padding: 10px 12px;
    }
    .two-factor__qr {
        display: flex;
        justify-content: center;
        margin: 0 0 20px;
    }
    .two-factor__qr img {
        width: 220px;
        height: 220px;
        border-radius: 12px;
        background: #fff;
    }
    .two-factor__choices .two-factor__continue--second {
        margin-top: 12px;
    }
    .two-factor__continue {
        width: 100%;
        margin-top: 28px;
        padding: 14px 24px;
        font-size: 16px;
        font-weight: 600;
        border-radius: 12px;
        border: none;
        background: linear-gradient(135deg, #19c7d0 0%, #17a2b8 100%);
        color: white;
        cursor: pointer;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }
    .two-factor__resend,
    .two-factor__back {
        display: block;
        width: 100%;
        margin-top: 12px;
        padding: 0;
        border: none;
        background: none;
        color: #19c7d0;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        text-align: center;
    }
    .two-factor.weflix .two-factor__input:focus {
        border-color: #0d1a7e;
        box-shadow: 0 0 0 0.2rem rgba(13, 26, 126, 0.2);
    }
    .two-factor.weflix .two-factor__continue {
        background: #0d1a7e;
        text-transform: none;
    }
    .two-factor.weflix .two-factor__resend,
    .two-factor.weflix .two-factor__back {
        color: #0d1a7e;
    }
    .two-factor.portal .two-factor__title {
        font-size: 1.5rem;
        color: #272f3a;
    }
    .two-factor.portal .two-factor__subtitle,
    .two-factor.portal .two-factor__label {
        color: #757e8a;
        text-transform: none;
        letter-spacing: 0;
        font-weight: 400;
        font-size: 0.9rem;
    }
    .two-factor.portal .two-factor__input {
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 1.25rem;
        background: white;
    }
    .two-factor.portal .two-factor__input:focus {
        border-color: #3db8bf;
        box-shadow: none;
    }
    .two-factor.portal .two-factor__continue {
        background: #3db8bf;
        border-radius: 4px;
        text-transform: none;
    }
    .two-factor.portal .two-factor__back {
        color: #3db8bf;
    }
    .two-factor.superadmin .two-factor__continue {
        background: #0d1a7e;
        border-radius: 5px;
        text-transform: none;
        padding: 10px;
    }
    .two-factor.superadmin .two-factor__input:focus {
        border-color: #0d1a7e;
        box-shadow: 0 0 0 0.2rem rgba(13, 26, 126, 0.2);
    }
    .two-factor.superadmin .two-factor__back {
        color: #0d1a7e;
    }
    .two-factor.modal .two-factor__title {
        text-align: left;
        font-size: 30px;
    }
    .two-factor.modal .two-factor__subtitle,
    .two-factor.modal .two-factor__hint {
        text-align: left;
    }
    .two-factor.modal .two-factor__label {
        text-transform: none;
        letter-spacing: 0;
        font-weight: 400;
        color: #757e8a;
    }
    .two-factor.modal .two-factor__input {
        border-radius: 4px;
        letter-spacing: 0.3em;
    }
    .two-factor.modal .two-factor__input:focus {
        border-color: #000;
        box-shadow: none;
    }
    .two-factor.modal .two-factor__continue {
        background: #000;
        border-radius: 5px;
        text-transform: none;
        padding: 18px 24px;
        font-size: 20px;
    }
    .two-factor.modal .two-factor__back {
        color: rgb(44, 158, 214);
        text-align: left;
    }
</style>
